<i18n>
{
	"en": {
		"newDriver": "New driver",
		"deleteDrivers": "Remove driver | Remove driver | Remove drivers",
		"noDriversFound": "No Drivers...",
		"header": "Drivers",
		"name": "Name",
		"phoneNumber": "Phonenumber",
		"email": "Email",
		"updatedDrivers": "Driver updated",
		"failedToUpdate": "Failed to update driver",
		"failedToFetch": "Failed to get driver data",
		"failedToDelete": "Failed to delete driver",
		"deletedDrivers": "Deleted driver | Deleted driver | Deleted {count} drivers"
	},
	"no": {
		"newDriver": "Ny sjåfør",
		"deleteDrivers": "Fjern sjåfør | Fjern sjåfør | Fjern sjåfører",
		"noDriversFound": "Ingen sjåfører...",
		"header": "Sjåfører",
		"name": "Navn",
		"phoneNumber": "Telefon",
		"email": "E-post",
		"updatedDrivers": "Sjåfør oppdatert",
		"failedToUpdate": "Kunne ikke oppdatere sjåfør",
		"failedToFetch": "Kunne ikke hente sjåfør data",
		"failedToDelete": "Kunne ikke slette sjåfør",
		"deletedDrivers": "Sjåføren ble slettet | Sjåføren ble slettet | Slettet {count} sjåfører"
	}
}
</i18n>
<template>
	<t-vertical-panel class="drivers-list">
		<div slot="header" class="drivers-list-header">
			<t-panel-header size="md" icon="person" type="bp">
				{{ $tc('header', driversFound) }}
			</t-panel-header>
		</div>
		<div slot="sub-header" class="drivers-list-sub-header">
			<t-button
				:disabled="rowsSelected == 0"
				iconBefore="trash"
				appearance="primary"
				intent="danger"
				type="bp"
				@click="onDeleteDrivers"
			>{{$tc('deleteDrivers', rowsSelected)}}</t-button>
			<t-button
				iconBefore="plus"
				appearance="primary"
				@click="onAddDrivers"
			>{{$t('newDriver')}}</t-button>
		</div>
		<div class="drivers-list-content">
			<t-grid
				v-if="driversFound"
				ref="grid"
				:columnDefinitions="columnDefinitions"
				:defaultColumnDefinition="defaultColumnDefinition"
				:rowHeight="40"
				:items="drivers"
				:selectableMode="['checkbox']"
				localStorageId="DriversSearchList"
				selectable
				@selected="onRowsSelected"
			>
				<template v-slot:name="{ item }">
					<t-text-input
						:value="item.name"
						@change="updateValue(item, 'name', $event)"
					/>
				</template>
				<template v-slot:phoneNumber="{ item }">
					<t-text-input
						:value="item.phoneNumber"
						@change="updateValue(item, 'phoneNumber', $event)"
					/>
				</template>
				<template v-slot:email="{ item }">
					<t-text-input
						:value="item.email"
						@change="updateValue(item, 'email', $event)"
					/>
				</template>
			</t-grid>
			<t-no-content-panel
				v-else
				:text="$t('noDriversFound')"
				icon="person"
				iconType="bp"
			/>
			<t-overlay
				:isShown="loading"
				absolute
				loader
			/>
		</div>
	</t-vertical-panel>
</template>

<script>
import { mapState, mapActions } from './store/drivers.store.js';
import Grid from '@/components/evergreen/timpex/grid/TGrid';

export default {
	name: 'Drivers',
	components: { TGrid: Grid, },
	data() {
		return {
			defaultColumnDefinition: {
				resizable: true,
				headerFilterInputWithButton: true,
			},
			rowsSelected: 0
		};
	},
	computed: {
		...mapState([
			'drivers',
			'loading',
		]),
		columnDefinitions() {
			return [
				{	field: 'name',
					displayName: this.$t('name'),
					width: 200 },
				{	field: 'phoneNumber',
					displayName: this.$t('phoneNumber'),
					minWidth: 125 },
				{	field: 'email',
					displayName: this.$t('email'),
					minWidth: 125 },
			];
		},
		driversFound() {
			return this.drivers.length;
		},
	},
	async mounted() {
		try {
			await this.getDrivers();
		} catch (e) {
			this.$notify({
				type: 'error',
				duration: -1,
				title: this.$t('failedToFetch')
			});
		}
	},
	beforeDestroy() {
		this.$notify({ clean: true });
	},
	methods: {
		...mapActions([
			'getDrivers',
			'updateDriver',
			'deleteDrivers',
			'createDriver',
		]),
		onRowsSelected(ids) {
			this.rowsSelected = ids.length;
		},
		async updateValue(driver, prop, data) {
			this.$notify({ clean: true });
			try {
				await this.updateDriver({ driver, prop, data });
				this.$notify({
					type: 'success',
					title: this.$t('updatedDrivers'),
				});
			} catch (e) {
				this.$notify({
					type: 'error',
					duration: -1,
					title: this.$t('failedToUpdate'),
				});
			}
		},
		async onDeleteDrivers() {
			let selectedDrivers = this.$refs.grid.getSelectedItems();
			if (!selectedDrivers) return;
			try {
				this.$notify({ clean: true });
				await this.deleteDrivers(selectedDrivers);
				this.$notify({
					type: 'success',
					title: this.$tc('deletedDrivers', selectedDrivers.length)
				});
				this.rowsSelected = 0;
			} catch (e) {
				this.$notify({
					type: 'error',
					duration: -1,
					title: this.$t('failedToDelete')
				});
			}
		},
		async onAddDrivers() {
			this.$notify({ clean: true });
			try {
				await this.createDriver({ name: '', phoneNumber: '', email: '' });
			} catch (e) {
				this.$notify({
					type: 'error',
					duration: -1,
					title: this.$t('failedToAdd')
				});
			}
		}
	},
};
</script>


<style lang="scss" scoped>
.drivers-list {
	.drivers-list-header {
		height: 100%;
		display: flex;
		align-items: center;
		padding-left: 15px;
	}
	.drivers-list-sub-header {
		display: flex;
		padding: 0px 15px;
		height: 100%;
		align-items: center;
		justify-content: flex-end;

		> button {
			margin: 10px;
		}
	}
	.drivers-list-content {
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 0px 5px
	}
}
</style>
